import type { Partner } from "@expert/shared-types";
import type { ButtonVariant } from "@mantine/core";
import type { ReactNode } from "react";

export interface GuideSessionContext {
    apiBaseUrl: string;
    callSid?: string;
    clientChannelId?: string;
    /** `isVerizonDeviceActivationApiEnabled` for this expert. Off (or absent) keeps Verizon on the manual flow. */
    isVerizonDeviceActivationApiEnabled?: boolean;
    /** Whether the app runs in production. Picks Verizon's production PIN encryption over its UAT one. */
    isProdMode?: boolean;
    /** The off-call session. It talks to Simplr without a ticket id (see `startSimplrSession`). */
    isIdleSession?: boolean;
    partner: Partner;
    sessionId: string;
    ssoAccessToken: string;
    taskMdn: string;
}

export interface SolveGuidedFlow {
    flowId: string;
    tag: string;
    firstStepId: string;
    steps: Record<string, SolveGuidedFlowStep>;
    tooltip?: ReactNode;
    feedbackOptions: GuideFeedbackOptions;
    reset: VoidFunction;
}

export interface SolveGuidedFlowStep {
    stepId: string;
    tag?: string;
    content: ReactNode;
    progress: number;
    imageUrl?: string;
    variant: GuideStepVariant;
    actions: SolveGuidedFlowAction[];
}

export interface SolveGuidedFlowAction {
    label: string;
    variant?: ButtonVariant;
    nextStepId?: string;
    nextStepAction?: "CloseGuide" | "PreviousStep";
    onClick?: VoidFunction;
    validate?: () => string | undefined;
}

export type GuideStepVariant = "Success" | "Error" | "Default";

export interface GuideFeedbackOptions {
    title: string;
    options: string[];
}

export interface OpenGuideSource {
    source: "auto" | "manual";
    sourceDetails?: GuideSourceDetails;
}
export type GuideSourceDetails =
    | "intent"
    | "queue"
    | "tfn"
    | "recommendation"
    | "call-start"
    | "toolbox"
    | "tab"
    | "in-progress-guide";

export type CloseGuideSource = "auto" | "closeButton" | "tabChange";

export const defaultHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

/** Rapid7 APIs for device activation may take over 10 seconds to complete */
export const defaultTimeoutInMS = 30_000;
